import React, { useCallback, useEffect, useState } from 'react';
import { Cpu, Save, RotateCcw, Loader } from 'lucide-react';

/**
 * AI agents: which Claude model each agent of the affiliate agent team runs
 * on, for every account. Saved on the server; the agents pick a change up
 * within half a minute, no deploy.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';

const AgentModelsCard = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/agent-team/models`, { credentials: 'include' });
      const body = await res.json();
      if (!body.success) throw new Error(body.message || 'Could not load the models.');
      setData(body.models);
      setDraft(Object.fromEntries(body.models.roles.map((r) => [r.key, r.model])));
    } catch (err) {
      setNotice({ ok: false, text: err.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changed = data ? data.roles.filter((r) => (draft[r.key] || '').trim() && draft[r.key].trim() !== r.model) : [];

  const setTier = (tier, model) => {
    if (!model) return;
    setDraft((d) => ({ ...d, ...Object.fromEntries(data.roles.filter((r) => r.tier === tier).map((r) => [r.key, model])) }));
  };

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const res = await fetch(`${API_BASE}/api/agent-team/models`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ models: Object.fromEntries(changed.map((r) => [r.key, draft[r.key].trim()])) })
      });
      const body = await res.json();
      if (!body.success) throw new Error(body.message || 'Could not save.');
      setData(body.models);
      setDraft(Object.fromEntries(body.models.roles.map((r) => [r.key, r.model])));
      setNotice({ ok: true, text: 'Saved. Every account\'s agents use these models within 30 seconds.' });
    } catch (err) {
      setNotice({ ok: false, text: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 mb-6">
      <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-slate-700">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-slate-100">
            <Cpu className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            AI agents
          </h2>
          <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
            The Claude model each affiliate agent runs on, for every account. A stronger model negotiates and writes better and costs more per run; the agents that only read and sort are left on the fast one.
            {data?.updated_at ? ` Last changed ${new Date(data.updated_at).toLocaleString()}${data.updated_by ? ` by ${data.updated_by}` : ''}.` : ' Using the server defaults.'}
          </p>
        </div>
        {notice && (
          <p className={`text-xs ${notice.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{notice.text}</p>
        )}
      </div>

      {!data ? (
        <div className="p-6 text-sm text-gray-500 dark:text-slate-400">{notice ? '' : 'Loading...'}</div>
      ) : (
        <div className="p-6 space-y-4">
          <datalist id="agent-team-models">
            {(data.options || []).map((id) => <option key={id} value={id} />)}
          </datalist>

          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-slate-400">
            <span>Set all at once:</span>
            {[['main', 'Main agents'], ['fast', 'Fast agents']].map(([tier, label]) => (
              <label key={tier} className="flex items-center gap-2">
                {label}
                <select className={`${inputCls} w-56`} value="" onChange={(e) => setTier(tier, e.target.value)}>
                  <option value="">Choose a model</option>
                  {(data.options || []).map((id) => <option key={id} value={id}>{id}</option>)}
                </select>
              </label>
            ))}
            {data.options_source !== 'anthropic' && <span>Could not list the account's models from Anthropic; type a model name if it is missing.</span>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.roles.map((r) => {
              const value = draft[r.key] ?? '';
              const dirty = value.trim() && value.trim() !== r.model;
              return (
                <div key={r.key} className={`rounded-lg border p-3 ${dirty ? 'border-blue-400 dark:border-blue-500' : 'border-gray-200 dark:border-slate-700'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-slate-100">{r.label}</span>
                    <span className="text-xs text-gray-500 dark:text-slate-400">{r.tier === 'fast' ? 'fast' : 'main'}</span>
                  </div>
                  <p className="mb-2 text-xs text-gray-500 dark:text-slate-400">{r.hint}</p>
                  <div className="flex items-center gap-2">
                    <input list="agent-team-models" className={inputCls} value={value} onChange={(e) => setDraft((d) => ({ ...d, [r.key]: e.target.value }))} />
                    {value !== r.default && (
                      <button type="button" title={`Back to the default (${r.default})`} onClick={() => setDraft((d) => ({ ...d, [r.key]: r.default }))}
                        className="text-gray-400 hover:text-gray-700 dark:hover:text-slate-200">
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {dirty && <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">Now {r.model}</p>}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <button type="button" onClick={save} disabled={!changed.length || saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
              {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save {changed.length ? `(${changed.length})` : ''}
            </button>
            {changed.length > 0 && (
              <button type="button" onClick={() => setDraft(Object.fromEntries(data.roles.map((r) => [r.key, r.model])))}
                className="text-sm text-gray-600 hover:underline dark:text-slate-400">Undo changes</button>
            )}
            <span className="text-xs text-gray-500 dark:text-slate-400">Each model is checked with Anthropic before it is saved.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentModelsCard;
